/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {Injectable} from '@angular/core';
import {PartNumberChangesDetailStateModel} from '../../part-number-changes-detail/part-number-changes-detail-state.model';
import {PartNumberChangesDetailConfigurationModel} from './models/part-number-changes-detail-configuration.model';
import {PartNumberChangesDetailHeaderConfigurationModel} from './models/part-number-changes-detail-header-configuration.model';
import {PartNumberChangesStatusEnum} from '../../models/part-number-changes-status.enum';
import {InputDisplayModeEnum} from '../../../shared/input/input-display-mode.enum';
import {PartNumberChangesDetailOldPartNumberCredentialsConfigurationModel} from './models/part-number-changes-detail-old-part-number-credentials-configuration.model';
import {PartNumberChangesDetailTrialConfigurationModel} from './models/pnc-trial/part-number-changes-detail-trial-configuration.model';
import {PartNumberChangesDetailTrialDisplayConfigurationModel} from './models/pnc-trial/part-number-changes-detail-trial-display-configuration.model';
import PNC_DISPLAY_CONFIGURATION_CONSTANTS from './part-number-changes-display-constants';
import {PartNumberChangesTrialModel} from './models/pnc-trial/part-number-changes-trial.model';
import {PartNumberChangesTrialStatusEnum} from './models/pnc-trial/part-number-changes-trial-status.enum';
import {AvailableActions} from '../../../api/ms-partnumberchanges/contracts/available-actions.enum';
import {PartNumberChangesDetailFooterConfigurationModel} from './models/part-number-changes-detail-footer-configuration.model';
import { v4 as uuid } from 'uuid';
import {UserBaseContract} from '../../../api/ms-partnumberchanges/contracts/user-base.contract';
import {PncTypeEnumMapper} from '../../models/pnc-type-enum/pnc-type.enum.mapper';
import {TechnicianGroupModelMapper} from '../../models/technician-group-model/technician-group.model.mapper';
import {PncDetailModel} from '../../models/pnc-detail/pnc-detail.model';
import {PartNumberChangesSolutionConfigurationModel} from './models/pnc-trial/part-number-changes-solution-configuration.model';
import {PartNumberChangesDetailTrialDisplayModeEnum} from './models/pnc-trial/part-number-changes-detail-trial-display-mode.enum';
import {PartNumberChangesDetailStickyHeaderConfigurationModel} from './models/part-number-changes-detail-sticky-header-configuration.model';
import {PartNumberChangesSolutionModel} from './models/part-number-changes-solution-configuration.model';
import {PncTypeEnum} from '../../models/pnc-type-enum/pnc-type.enum';
import {PartNumberChangesSolutionDisplayConfigurationModel} from './models/part-number-changes-solution-display-configuration.model';
import {PartNumberChangesCredentialsSectionConfigurationModel} from './models/part-number-changes-credentials-section-configuration.model';
import {PartNumberChangeStatusTimelineHistoryModel} from '../../models/part-number-change-status-timeline-history.model';
import {PncStatusColorEnum} from '../../models/pnc-status-color.enum';
import {PartNumberChangesCancellationConfigurationModel} from './models/cancellation/part-number-changes-cancellation-configuration.model';
import {PartNumberChangeSolutionTrialModel} from './models/pnc-solution-trials/part-number-change-solution-trial.model';
import {PartNumberChangeDetailSolutionTrialConfigurationModel} from './models/pnc-solution-trials/part-number-change-detail-solution-trial-configuration.model';

@Injectable({
    providedIn: 'root'
})
export class PartNumberChangesDetailSectionsConfigurationService {

    private readonly colorStatus: ReadonlyMap<PartNumberChangesStatusEnum, string> = new Map<PartNumberChangesStatusEnum, string>([
        [PartNumberChangesStatusEnum.SOLVED, 'solved'],
        [PartNumberChangesStatusEnum.ANALYSIS, 'analysis'],
        [PartNumberChangesStatusEnum.PENDING, 'pending'],
        [PartNumberChangesStatusEnum.REJECTED, 'rejected'],
        [PartNumberChangesStatusEnum.CANCELLED, 'cancelled']
    ]);

    private readonly statusTimelineColors: ReadonlyMap<PncStatusColorEnum, string> = new Map<PncStatusColorEnum, string>([
        [PncStatusColorEnum.BACKGROUND_COLOR, '#f6f6f6'],
        [PncStatusColorEnum.CREATION, '#ffffff'],
        [PncStatusColorEnum.PENDING, '#fecb00'],
        [PncStatusColorEnum.ANALYSIS, '#b3caff'],
        [PncStatusColorEnum.REJECTED, '#eabebf'],
        [PncStatusColorEnum.SOLVED, '#526482'],
        [PncStatusColorEnum.CANCELLED, '#910B17']
    ]);

    private readonly doneFlagLabel = 'Done';

    /**
     * For a given PNC detail returns its display configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     * @param state the {@link PartNumberChangesDetailStateModel} for the PNC detail
     */
    retrievePNCDetailSectionsConfiguration(pncDetail: PncDetailModel,
                                           state: PartNumberChangesDetailStateModel):
            PartNumberChangesDetailConfigurationModel {
        let configuration: PartNumberChangesDetailConfigurationModel = <PartNumberChangesDetailConfigurationModel>{};
        if (pncDetail != null) {
            configuration = {
                stickyHeader: this.getStickyHeaderConfiguration(pncDetail),
                header: this.getHeaderConfiguration(pncDetail),
                credentials: this.getCredentialsConfiguration(pncDetail),
                oldPartNumberCredentials: this.getOldPartNumberCredentialsConfiguration(pncDetail),
                footer: this.getFooterConfiguration(pncDetail, state),
                solution: this.getSolutionConfiguration(pncDetail),
                cancellation: this.getCancellationConfiguration(pncDetail)
            };
        }
        return configuration;
    }

    /**
     * For a given PCN detail returns its sticky header section configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getStickyHeaderConfiguration(pncDetail: PncDetailModel): PartNumberChangesDetailStickyHeaderConfigurationModel {
        return {
            data: pncDetail,
            done: pncDetail.done
        };
    }

    /**
     * For a given PCN detail returns its header section configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getHeaderConfiguration(pncDetail: PncDetailModel): PartNumberChangesDetailHeaderConfigurationModel {
        return {
            type: pncDetail.type?.name,
            styleClass: this.getHeaderCssStyle(<PartNumberChangesStatusEnum>pncDetail.status?.code),
            status: pncDetail.status?.name
        };
    }

    private getHeaderCssStyle(statusCode: PartNumberChangesStatusEnum): string {
        return this.colorStatus.get(statusCode);
    }

    /**
     * For a given PCN detail returns its credentials section configuration
     * @param pncDetail the {@link PartNumberChangeDetailModel} for the PNC
     */
    private getCredentialsConfiguration(pncDetail: PncDetailModel): PartNumberChangesCredentialsSectionConfigurationModel {
        return {
            credentials: {
                pncType: pncDetail?.type,
                displayMode: InputDisplayModeEnum.VIEW,
                partner: pncDetail.partner,
                supplyingPackingPlant: pncDetail.supplyingPackingPlant,
                shipToParty: pncDetail.shipToParty,
                country: pncDetail.country
            },
            sectionTitle: PNC_DISPLAY_CONFIGURATION_CONSTANTS.title.credentials,
            statusTimelineConfiguration: {
                timelineHistory: this.getStatusTimelineHistory(pncDetail),
                statusColors: this.statusTimelineColors
            }
        };
    }

    /**
     * For a given PCN detail returns its status timeline history model with initialState, previousStates and currentState
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getStatusTimelineHistory(pncDetail: PncDetailModel): PartNumberChangeStatusTimelineHistoryModel[] {
        const timeline: PartNumberChangeStatusTimelineHistoryModel[] = [];

        // Add creation state
        timeline.push({
            createdBy: pncDetail.createdBy,
            createdOn: pncDetail.creationDatetime,
            status: pncDetail.type,
            requiresFlashing: pncDetail.requiresFlashing ?? null
        });

        // Add previous states
        timeline.push(...pncDetail.previousStatus.sort(
                function (a: PartNumberChangeStatusTimelineHistoryModel,
                          b: PartNumberChangeStatusTimelineHistoryModel) {
                    return a.createdOn - b.createdOn;
                }));

        // Add current state
        timeline.push({
            createdBy: pncDetail.lastSentBy,
            createdOn: this.getCurrentStateTimestamp(pncDetail),
            status: pncDetail.status,
            flag: pncDetail.done ? this.doneFlagLabel : null
        });

        return timeline;
    }

    private getCurrentStateTimestamp(pncDetail: PncDetailModel): number {
        const statusCode = pncDetail?.status?.code;
        let timestamp: number;

        switch (statusCode) {
            case PartNumberChangesStatusEnum.SOLVED:
                timestamp = pncDetail?.lastSentOn;
                break;
            case PartNumberChangesStatusEnum.CANCELLED:
                timestamp = pncDetail?.cancelledOn;
                break;
            default:
                timestamp = pncDetail?.creationDatetime;
        }

        if (statusCode === PartNumberChangesStatusEnum.SOLVED && pncDetail.requiresFlashing) {
            timestamp = pncDetail?.solvedOn;
        }
        return timestamp;
    }

    /**
     * For a given PCN detail returns its old part number and trials section configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getOldPartNumberCredentialsConfiguration(pncDetail: PncDetailModel):
            PartNumberChangesDetailOldPartNumberCredentialsConfigurationModel {
        return {
            showCurrent: this.getShowCurrentOldPartNumberCredentials(pncDetail),
            current: this.getCurrentOldPartNumberCredentials(pncDetail),
            trials: this.getTrials(pncDetail)
        };
    }

    /**
     * For a given PCN detail returns its trials section configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getTrials(pncDetail: PncDetailModel): PartNumberChangesDetailTrialConfigurationModel[] {
        // TODO: CTWILS-3882 - Update to change configuration according to the pncType
        const trials: PartNumberChangesDetailTrialConfigurationModel[] = [];

        const configuration: PartNumberChangesDetailTrialDisplayConfigurationModel =
                PNC_DISPLAY_CONFIGURATION_CONSTANTS.oldPartNumber.view;

        if (pncDetail.trials != null) {
            const pncType = PncTypeEnumMapper.fromString(pncDetail?.type?.code);
            const pncStatus = PncTypeEnumMapper.fromString(pncDetail?.status?.code);

            let count = 1;

            pncDetail.trials.forEach((trial) => {
                const newTrial: PartNumberChangesTrialModel = {
                    pncType,
                    title: this.getTrialTitle(count, PartNumberChangesTrialStatusEnum.REJECTED),
                    material: trial.material,
                    salesVariant: trial.salesVariant,
                    modelCode: trial.modelCode,
                    colourCode: trial?.colourCode,
                    fullLotNumber: trial.fullLotNumber,
                    additionalFullLotNumber: trial?.additionalFullLotNumbers,
                    quantity: trial.quantity,
                    problemDescription: trial?.problemDescription,
                    sentBy: trial.sentBy,
                    sentOn: trial.sentOn,
                    status: pncStatus,
                    spoNumber: trial.spoNumber,
                    additionalSpoNumbers: trial.additionalSpoNumbers,
                    trialRejectionDetail: {
                        motivationToReject: trial.motivationToReject,
                        rejectedBy: trial.rejectedBy,
                        rejectedOn: trial.rejectedOn
                    }
                };
                trials.push({
                    data: newTrial,
                    displayConfiguration: configuration
                });
                count = count + 1;
            });
        }
        return trials;
    }

    private getTrialTitle(count: number, status?: PartNumberChangesTrialStatusEnum): string {
        return status != null ? `Trial ${count} (${status})` : `Trial ${count}`;
    }

    /**
     * For a given PNC detail return if the current PNC details should be displayed
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getShowCurrentOldPartNumberCredentials(pncDetail: PncDetailModel): boolean {
        let show = false;

        if ((pncDetail.status?.code === PartNumberChangesStatusEnum.ANALYSIS
                        || pncDetail.trials == null
                        || pncDetail.trials.length === 0)
                || pncDetail.availableActions?.includes(AvailableActions.SEND)
                || (pncDetail.status.code === PartNumberChangesStatusEnum.SOLVED)) {
            show = true;
        }

        return show;
    }

    /**
     * For a given PCN detail returns its current PNC details configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getCurrentOldPartNumberCredentials(pncDetail: PncDetailModel):
            PartNumberChangesDetailTrialConfigurationModel {

        return {
            displayConfiguration: this.getCurrentOldPartNumberCredentialsDisplayConfiguration(pncDetail),
            data: this.getCurrentOldPartNumberCredentialsData(pncDetail)
        };
    }

    /**
     * For a given PCN detail returns its current PNC details display data
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getCurrentOldPartNumberCredentialsData(pncDetail: PncDetailModel): PartNumberChangesTrialModel {
        return {
            pncType: pncDetail?.type.code,
            title: this.getTrialTitle(this.getCurrentPNCCount(pncDetail),
                    this.getCurrentOldPartNumberCredentialsStatus(<PartNumberChangesStatusEnum>pncDetail.status.code,
                            pncDetail.requiresFlashing)),
            material: pncDetail?.material,
            salesVariant: pncDetail?.salesVariant,
            modelCode: pncDetail?.modelCode,
            colourCode: pncDetail?.colourCode,
            fullLotNumber: pncDetail?.fullLotNumber,
            additionalFullLotNumber: pncDetail?.additionalFullLotNumbers,
            quantity: pncDetail?.quantity,
            problemDescription: pncDetail?.problemDescription,
            sentBy: this.getCurrentOldPartNumberSendBy(pncDetail),
            sentOn: pncDetail?.lastChangeDatetime,
            status: pncDetail?.status?.code,
            technicianGroup: TechnicianGroupModelMapper.fromTechnicianGroupBaseContract(pncDetail.technicianGroup),
            spoNumber: pncDetail?.spoNumber,
            additionalSpoNumbers: pncDetail?.additionalSpoNumbers
        };
    }

    /**
     * For a given PCN detail returns its current PNC details display configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getCurrentOldPartNumberCredentialsDisplayConfiguration(pncDetail: PncDetailModel):
            PartNumberChangesDetailTrialDisplayConfigurationModel {

        const configuration: PartNumberChangesDetailTrialDisplayConfigurationModel = {
            ...PNC_DISPLAY_CONFIGURATION_CONSTANTS.oldPartNumber.view,
            styleClass: this.getCurrentOldPartNumberCredentialsStyleClass(<PartNumberChangesStatusEnum>pncDetail.status?.code,
                    pncDetail.requiresFlashing),
            isSolutionAccepted: this.getIsSolutionAccepted(<PartNumberChangesStatusEnum>pncDetail.status?.code, pncDetail.requiresFlashing)
        };

        configuration.pncType = PncTypeEnumMapper.fromString(pncDetail.type?.code);

        const hasPartnerPermissions: boolean = pncDetail.availableActions?.includes(AvailableActions.SEND)
                || pncDetail.availableActions?.includes(AvailableActions.CREATE);
        const requiresPartnerIntervention: boolean = pncDetail.status?.code === PartNumberChangesStatusEnum.REJECTED
                && hasPartnerPermissions;
        const requiresPartnerInterventionPending: boolean = pncDetail.status?.code === PartNumberChangesStatusEnum.PENDING
                && hasPartnerPermissions;

        if (requiresPartnerIntervention) {
            configuration.salesVariantInput = {displayMode: InputDisplayModeEnum.EDIT};
            configuration.modelCodeInput = {displayMode: InputDisplayModeEnum.EDIT, required: true};
            configuration.colourInput = {displayMode: InputDisplayModeEnum.EDIT};
            configuration.fullLotNumberInput = {displayMode: InputDisplayModeEnum.EDIT, required: true};
            configuration.descriptionInput = {displayMode: InputDisplayModeEnum.EDIT};
            configuration.spoNumberInput = {displayMode: InputDisplayModeEnum.EDIT};
        }

        if (requiresPartnerInterventionPending) {
            configuration.salesVariantInput = {displayMode: InputDisplayModeEnum.EDIT};
            configuration.modelCodeInput = {displayMode: InputDisplayModeEnum.EDIT, required: true};
            configuration.colourInput = {displayMode: InputDisplayModeEnum.EDIT};
            configuration.fullLotNumberInput = {displayMode: InputDisplayModeEnum.EDIT, required: true};
            configuration.descriptionInput = {displayMode: InputDisplayModeEnum.EDIT};
            configuration.spoNumberInput = {displayMode: InputDisplayModeEnum.EDIT};
        }

        configuration.partNumberChangeConfig = pncDetail.partNumberChangeConfig;

        return configuration;
    }

    /**
     * Returns the current PNC details trial count
     */
    private getCurrentPNCCount(pncDetail: PncDetailModel): number {
        return pncDetail.trials != null ? pncDetail.trials.length + 1 : 1;
    }

    /**
     * Returns the current PNC details status
     */
    private getCurrentOldPartNumberCredentialsStatus(status: PartNumberChangesStatusEnum,
                                                     requiresFlashing: boolean): PartNumberChangesTrialStatusEnum {
        return status === PartNumberChangesStatusEnum.SOLVED && !requiresFlashing ? PartNumberChangesTrialStatusEnum.ACCEPTED : null;
    }

    /**
     * Returns the current PNC details general css class
     */
    private getCurrentOldPartNumberCredentialsStyleClass(status: PartNumberChangesStatusEnum, requiresFlashing: boolean): string {
        let style = '';
        if (status === PartNumberChangesStatusEnum.REJECTED) {
            style = 'edit';
        } else if (!requiresFlashing && status === PartNumberChangesStatusEnum.SOLVED){
            style = 'status-accepted';
        }
        return style;
    }

    /**
     * Returns if the current PNC solution is accepted
     */
    private getIsSolutionAccepted(status: PartNumberChangesStatusEnum, requiresFlashing: boolean): boolean {
        return !requiresFlashing && status === PartNumberChangesStatusEnum.SOLVED;
    }

    /**
     * For a given PCN detail returns its footer section configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     * @param state the {@link PartNumberChangesDetailStateModel} for the PNC detail
     */
    private getFooterConfiguration(pncDetail: PncDetailModel,
                                   state: PartNumberChangesDetailStateModel): PartNumberChangesDetailFooterConfigurationModel {
        let configuration: PartNumberChangesDetailFooterConfigurationModel = null;

        if (pncDetail.availableActions?.includes(AvailableActions.SEND_SOLUTION)) {
            if (pncDetail.availableActions?.includes(AvailableActions.REJECT) || pncDetail.availableActions?.includes(
                    AvailableActions.SOLVE)) {
                configuration = state.isSolving
                        ? PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.technicianSolve
                        : PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.technicianRejectAccept;
            } else {
                configuration = PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.pncSendToEESpecialist;
            }
        }

        if (pncDetail.availableActions?.includes(AvailableActions.SEND)) {
            configuration = PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.pncSendTrial;

            if (pncDetail.availableActions?.includes(AvailableActions.CANCEL)) {
                configuration = PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.pncSendOrCancelTrial;
            }
        }

        if (pncDetail.availableActions?.includes(AvailableActions.REJECT_SOLUTION)) {
            configuration = PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.pncSolvedEESpecialist;
        }

        return configuration;
    }

    private getCurrentOldPartNumberSendBy(detail: PncDetailModel): UserBaseContract {
        return (detail?.status.code === PartNumberChangesStatusEnum.REJECTED) ? null : detail?.lastSentBy;
    }

    /**
     * For a given PCN detail returns its solution trials section configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getSolutionTrials(pncDetail: PncDetailModel): PartNumberChangeDetailSolutionTrialConfigurationModel[] {
        const trials: PartNumberChangeDetailSolutionTrialConfigurationModel[] = [];

        const configuration: PartNumberChangesSolutionDisplayConfigurationModel =
                PNC_DISPLAY_CONFIGURATION_CONSTANTS.solution.solutionTrials;

        if (pncDetail?.solutionTrials != null) {
            let count = 1;

            pncDetail?.solutionTrials?.forEach((solutionTrial) => {
                const newTrial: PartNumberChangeSolutionTrialModel = {
                    title: this.getTrialTitle(count, PartNumberChangesTrialStatusEnum.REJECTED),
                    newPartNumber: {
                        material: solutionTrial?.material,
                        quantity: solutionTrial?.quantity?.toString(),
                        isValid: null,
                        id: uuid()
                    },
                    additionalMaterials: solutionTrial?.additionalMaterials?.map((additional) => ({
                        material: additional?.material,
                        quantity: additional?.quantity?.toString(),
                        isValid: null,
                        id: uuid()
                    })),
                    additionalInformation: solutionTrial.additionalInformation,
                    sentBy: solutionTrial.sentBy,
                    trialRejectionDetail: {
                        motivationToReject: solutionTrial.motivationToReject,
                        rejectedBy: solutionTrial.rejectedBy
                    }
                };
                trials.push({
                    data: newTrial,
                    displayConfiguration: configuration
                });
                count = count + 1;
            });
        }
        return trials;
    }

    /**
     * For a given PCN detail returns its solution section configuration
     * @param pncDetail the {@link PncDetailModel} for the PNC
     */
    private getSolutionConfiguration(pncDetail: PncDetailModel):
            PartNumberChangesSolutionConfigurationModel {
        return {
            isSolutionEE: false,
            displayConfiguration: this.getSolutionDisplayConfiguration(pncDetail),
            data: this.getSolutionData(pncDetail),
            solutionTrials: this.getSolutionTrials(pncDetail),
            requiresFlashing: pncDetail?.requiresFlashing,
            alternativeSupplyingPackingPlants: pncDetail.alternativeSupplyingPackingPlants,
            solutionSupplyingPackingPlant: pncDetail.solutionSupplyingPackingPlant,
            type: pncDetail?.type
        };
    }

    private getSolutionDisplayConfiguration(pncDetail: PncDetailModel):
            PartNumberChangesSolutionDisplayConfigurationModel {

        let configuration: PartNumberChangesSolutionDisplayConfigurationModel;

        if (pncDetail.status?.code === PartNumberChangesStatusEnum.SOLVED
                || pncDetail.status?.code === PartNumberChangesStatusEnum.EE_ANALYSIS) {
            configuration = {
                displayMode: pncDetail.status?.code === PartNumberChangesStatusEnum.SOLVED
                        ? PartNumberChangesDetailTrialDisplayModeEnum.DETAIL
                        : PartNumberChangesDetailTrialDisplayModeEnum.DETAIL_SOLUTION,
                quantityInput: {displayMode: InputDisplayModeEnum.VIEW},
                materialInput: {displayMode: InputDisplayModeEnum.VIEW},
                additionalInformation: {displayMode: InputDisplayModeEnum.VIEW},
                partNumberChangeConfig: pncDetail.partNumberChangeConfig,
                //alternativeSupplyingPackingPlants: pncDetail.alternativeSupplyingPackingPlants
            };
        }

        if (pncDetail.status?.code === PartNumberChangesStatusEnum.ANALYSIS) {
            configuration = {
                displayMode: PartNumberChangesDetailTrialDisplayModeEnum.CREATE,
                quantityInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
                materialInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
                additionalInformation: {displayMode: InputDisplayModeEnum.EDIT},
                partNumberChangeConfig: pncDetail.partNumberChangeConfig
            };

            if (pncDetail.type?.code === PncTypeEnum.F2_MISSING_PARTS) {
                configuration = {...configuration, quantityInput: {displayMode: InputDisplayModeEnum.EDIT}};
            }
        }

        configuration = {...configuration, partNumberChangeConfig: pncDetail.partNumberChangeConfig};

        return configuration;
    }

    private getSolutionData(pncDetail: PncDetailModel): PartNumberChangesSolutionModel {
        return {
            oldPartNumber: pncDetail?.material,
            newPartNumber: {
                material: pncDetail?.newMaterial?.material,
                quantity: pncDetail?.newMaterial?.quantity?.toString(),
                isValid: null,
                id: uuid()
            },
            additionalInformation: pncDetail?.additionalInformation,
            additionalMaterials: pncDetail?.additionalMaterials?.map((additional) => ({
                material: additional?.material,
                quantity: additional?.quantity?.toString(),
                isValid: null,
                id: uuid()
            })),
            solvedBy: pncDetail.status?.code === PartNumberChangesStatusEnum.EE_ANALYSIS ? pncDetail?.user : pncDetail?.solvedBy,
            eeAdditionalInformation: pncDetail?.eeAdditionalInformation,
            iLevel: pncDetail?.iLevel,
            validatedBy: pncDetail?.validatedBy
        };
    }

    private getCancellationConfiguration(pncDetail: PncDetailModel): PartNumberChangesCancellationConfigurationModel {
        return {
            oldPartNumber: pncDetail?.material,
            cancellationReason: pncDetail?.cancellationReason,
            cancelledBy: pncDetail?.cancelledBy
        };
    }
}
